import { A } from '@adornis/base/env-info.js';
import type { ColorType } from '@adornis/chemistry/elements/theming/coloring-system.js';
import type { Renderable } from '@adornis/functional-lit/renderable.js';
import fsppLogo from '@ils/assets/FSPP_Logo.png';
import fsppLogoSmall from '@ils/assets/FSPP_Logo_small.png';
import famedLogo from '@ils/assets/logo.png';
import famedLogoSmall from '@ils/assets/logo_small.png';
import putsLogo from '@ils/assets/PuTs_Logo.png';
import putsLogoSmall from '@ils/assets/PuTs_logo_small.png';
import { ExamType } from '@ils/static/enums.js';
import { famedThemeDefinition, fsppThemeDefinition } from '@ils/theme/theme.js';
import { html } from 'lit';

export interface ExamTypeBrandingdata {
  printables: {
    invoice: {
      productName: (...args: any[]) => Renderable;
    };
    certificate: {
      productName: (...args: any[]) => Renderable;
      footerNotice: () => Renderable;
    };
    result: {
      header: () => Renderable;
      productName: (...args: any[]) => Renderable;
      footerNotice: () => Renderable;
    };
  };
  colors: { primary: ColorType; secondary: ColorType };
  logos: { small: string; big: string };
}

export const examTypeBrandingData = {
  [ExamType.FAMED]: {
    printables: {
      invoice: {
        productName: (subtleText?: string) => html`
          <x-text> Fachsprachenprüfung Medizin nach Vorgaben des Beschlusses der 87. GMK </x-text>
          <x-text tone="subtle"> ${subtleText} </x-text>
        `,
      },
      certificate: {
        productName: () =>
          'die Fachsprachenprüfung Medizin C1 (gemäß den Vorgaben der 87. Gesundheitsministerkonferenz 2014) bestanden.',
        footerNotice: () =>
          'Diese Urkunde allein berechtigt nicht zur Ausübung des ärztlichen Berufes in der Bundesrepublik Deutschland. Die Arztbezeichnung darf nur führen, wer die Approbation als Arzt oder Ärztin oder eine Erlaubnis zur vorübergehenden Ausübung des ärztlichen Berufes in der Bundesrepublik Deutschland besitzt.',
      },
      result: {
        header: () => 'Ergebnis der Fachsprachenprüfung Medizin',
        productName: () =>
          'Die Bewertung der fachsprachlichen Prüfungsleistung erfolgte nach den Bestimmungen des Sprachniveaus C1 gemäß den Grundlagen des Gemeinsamen Europäischen Referenzrahmens (GER)',
        footerNotice: () =>
          html`
            <x-text tone="subtle">
              Dieses Schreiben dient der Information, gilt nicht als urkundlicher Nachweis der bestandenen
              Fachsprachprüfung und trägt deshalb keine Unterschrift.
            </x-text>
            <x-text tone="subtle">
              Es ist ausschließlich für den/die oben namentlich genannte/n Empfänger/in bestimmt.
            </x-text>
          `,
      },
    },
    colors: {
      primary: famedThemeDefinition.colors.primary,
      secondary: famedThemeDefinition.colors.secondary,
    },
    logos: {
      small: A.absoluteUrl(famedLogoSmall),
      big: A.absoluteUrl(famedLogo),
    },
  },
  [ExamType.FSPP]: {
    printables: {
      invoice: {
        productName: () => html`
          <x-text>
            Fachsprachenprüfung Pflege nach Vorgaben des Beschlusses von Bernd. TODO Ändern wenn Beschluss gefasst
          </x-text>
        `,
      },
      certificate: {
        productName: () =>
          'die Fachsprachenprüfung Pflege XY (gemäß den Vorgaben von Bernd TODO Ändern sobald beschlossen!) bestanden.',
        footerNotice: () =>
          'TODO Langer Text welcher darauf hinweist dass diese Urkunde allein nicht für irgendwas berechtigt, sondern eine weitere Anerkennung notwendig ist',
      },
      result: {
        header: () => 'Ergebnis der Fachsprachenprüfung Pflege',
        productName: () =>
          'die Fachsprachenprüfung Pflege XY (gemäß den Vorgaben von Bernd TODO Ändern sobald beschlossen!) bestanden.',
        footerNotice: () =>
          html`
            <x-text tone="subtle">
              Dieses Schreiben dient der Information, gilt nicht als urkundlicher Nachweis der bestandenen
              Fachsprachprüfung und trägt deshalb keine Unterschrift.
            </x-text>
            <x-text tone="subtle">
              Es ist ausschließlich für den/die oben namentlich genannte/n Empfänger/in bestimmt.
            </x-text>
          `,
      },
    },
    colors: {
      primary: fsppThemeDefinition.colors.primary,
      secondary: fsppThemeDefinition.colors.secondary,
    },
    logos: {
      small: A.absoluteUrl(fsppLogoSmall),
      big: A.absoluteUrl(fsppLogo),
    },
  },
  platform: {
    printables: {
      invoice: {
        productName: () => 'The Platform brand metadata only exists for the colors anf the logos',
      },
      certificate: {
        productName: () => 'The Platform brand metadata only exists for the colors anf the logos',
        footerNotice: () => 'The Platform brand metadata only exists for the colors anf the logos',
      },
      result: {
        header: () => 'The Platform brand metadata only exists for the colors anf the logos',
        productName: () => 'The Platform brand metadata only exists for the colors anf the logos',
        footerNotice: () => 'The Platform brand metadata only exists for the colors anf the logos',
      },
    },
    colors: {
      primary: famedThemeDefinition.colors.accent,
      secondary: famedThemeDefinition.colors.secondary,
    },
    logos: {
      small: A.absoluteUrl(putsLogoSmall),
      big: A.absoluteUrl(putsLogo),
    },
  },
} as const satisfies Record<ExamType | 'platform', ExamTypeBrandingdata>;
